import React, { createContext, useContext, useEffect, useState } from 'react';
import { getLocalStorageItem, setLocalStorageItem, getUserSetting, setUserSetting } from '../utils/localStorage';
import { useUser } from './UserContext';
import { api } from '../utils/apiClient';

type ThemeMode = 'light' | 'dark' | 'auto';

interface ThemeContextType {
  themeMode: ThemeMode;
  setThemeMode: (mode: ThemeMode) => void;
  defaultThemeMode: ThemeMode;
  setDefaultThemeMode: (mode: ThemeMode) => void;
  isLoadingTheme: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const { user } = useUser();
  const [themeMode, setThemeModeState] = useState<ThemeMode>('light');
  const [defaultThemeMode, setDefaultThemeModeState] = useState<ThemeMode>('light');
  const [isLoadingTheme, setIsLoadingTheme] = useState<boolean>(false);

  // Load theme settings - user specific or default
  useEffect(() => {
    const loadThemeSettings = async () => {
      setIsLoadingTheme(true);
      
      // Get the default theme (for non-logged-in users)
      const savedDefaultTheme = getLocalStorageItem<ThemeMode>('themeMode', 'light');
      setDefaultThemeModeState(savedDefaultTheme);
      
      if (user.email && user.token) {
        try {
          // Try to get settings from the server
          const response = await api.user.getSettings(user.email);
          if (response.success && response.settings) {
            // Use server settings if available
            const serverThemeMode = response.settings.themeMode as ThemeMode;
            setThemeModeState(serverThemeMode);
            applyTheme(serverThemeMode);
            
            // Also update localStorage for faster loading next time
            setUserSetting(user.email, 'themeMode', serverThemeMode);
          } else {
            // Fall back to localStorage if server request fails
            const userTheme = getUserSetting<ThemeMode>(user.email, 'themeMode', savedDefaultTheme);
            setThemeModeState(userTheme);
            applyTheme(userTheme);
            
            // Try to sync localStorage setting to the server
            if (userTheme !== 'auto') {
              api.user.updateSettings(user.email, { themeMode: userTheme });
            }
          }
        } catch (error) {
          // Use localStorage as fallback
          const userTheme = getUserSetting<ThemeMode>(user.email, 'themeMode', savedDefaultTheme);
          setThemeModeState(userTheme);
          applyTheme(userTheme);
        } finally {
          setIsLoadingTheme(false);
        }
      } else {
        // If not logged in, use default theme from localStorage
        setThemeModeState(savedDefaultTheme);
        applyTheme(savedDefaultTheme);
        setIsLoadingTheme(false);
      }
    };

    // Load theme settings initially
    loadThemeSettings();

    // Listen for consent changes to reload settings when consent is given
    const handleConsentChanged = () => {
      loadThemeSettings();
    };

    // Add listener for the custom consent event
    window.addEventListener('localStorageConsentChanged', handleConsentChanged);

    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener('localStorageConsentChanged', handleConsentChanged);
    };
  }, [user.email, user.token]); // Re-run when email or token changes (login/logout)

  const applyTheme = (mode: ThemeMode) => {
    if (mode === 'auto') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      document.documentElement.setAttribute('data-bs-theme', prefersDark ? 'dark' : 'light');
    } else {
      document.documentElement.setAttribute('data-bs-theme', mode);
    }
  };

  // Set up listener for system theme changes
  useEffect(() => {
    // Listen for system theme changes if in auto mode
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleSystemThemeChange = (e: MediaQueryListEvent) => {
      if (themeMode === 'auto') {
        document.documentElement.setAttribute('data-bs-theme', e.matches ? 'dark' : 'light');
      }
    };

    mediaQuery.addEventListener('change', handleSystemThemeChange);
    return () => {
      mediaQuery.removeEventListener('change', handleSystemThemeChange);
    };
  }, [themeMode]);

  const handleThemeChange = async (newMode: ThemeMode) => {
    setThemeModeState(newMode);
    
    // Save theme based on login state
    if (user.email && user.token) {
      // Save user-specific setting to localStorage
      setUserSetting(user.email, 'themeMode', newMode);
      
      // Also save to database
      try {
        await api.user.updateSettings(user.email, { themeMode: newMode });
      } catch (error) {
        // Silent fail - local settings will still be used
      }
    } else {
      // Save default setting
      setLocalStorageItem('themeMode', newMode);
      setDefaultThemeModeState(newMode);
    }
    
    // Apply the theme
    applyTheme(newMode);
  };

  const handleDefaultThemeChange = (newMode: ThemeMode) => {
    setDefaultThemeModeState(newMode);
    setLocalStorageItem('themeMode', newMode);
    
    // If not logged in, also update the current theme
    if (!user.email) {
      setThemeModeState(newMode);
      applyTheme(newMode);
    }
  };

  return (
    <ThemeContext.Provider value={{ 
      themeMode, 
      setThemeMode: handleThemeChange,
      defaultThemeMode,
      setDefaultThemeMode: handleDefaultThemeChange,
      isLoadingTheme
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
} 